import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { PartnerService } from 'src/app/services/partner.service';
import { ChecklistService } from 'src/app/services/checklist.service';
import { ServiceRequestService } from 'src/app/services/servicerequest.service';
import { HelpersService } from 'src/app/services/helpers.service';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { WorkshopJobcardCreateService } from '../workshop-jobcard-create.service';
import { catchError, debounceTime, distinctUntilChanged, of, Subject, Subscription, switchMap } from 'rxjs';
import { ConfirmationService, MessageService } from 'primeng/api';

@Component({
  selector: 'app-workshop-jobcard-booking-details',
  templateUrl: './workshop-jobcard-booking-details.component.html',
  styleUrls: ['./workshop-jobcard-booking-details.component.scss'],
  providers: [MessageService]
})
export class WorkshopJobcardBookingDetailsComponent implements OnChanges {

  @Input() jobcard: any;
  @Input() serviceTypes: any = [];
  @Input() technicians: any = [];
  @Input() systemUsers: any = [];
  @Input() thirdParties: any = [];
  @Output() jobcardChange = new EventEmitter<any>();

  public minDate: Date = new Date();
  public minRequiredByDate: Date = new Date();
  public serviceTypeGroups: any[] = [];
  public selectedServiceTypes: any[] = [];

  public scheduleDate = new Date();
  public scheduleTime: string = '';
  public requiredByDate: Date = new Date();
  public requiredByTime: string = '';
  public partnerData: any;
  public addRequiredByDate: boolean = false;
  public showThirdParties: boolean = false;
  public loadingDates: boolean = false;

  public bays: any[] = [];
  public bay: any = null;
  public tags: any[] = [];
  public linkedTags: any[] = [];
  public selectedTagIds: any[] = [];

  private bayTagUpdateQueue$ = new Subject<string>();

  @ViewChild('technicianDropdown') technicianDropdown: any;
  @ViewChild('createdByDropdown') createdByDropdown: any;
  @ViewChild('checkedByDropdown') checkedByDropdown: any;
  @ViewChild('thirdPartyDropdown') thirdPartyDropdown: any;
  @ViewChild('bayDropdown') bayDropdown: any;

  private updateQueue$ = new Subject<Partial<any>>();
  private subs = new Subscription();

  constructor(
    private partnerService: PartnerService,
    private helpersService: HelpersService,
    private checklistService: ChecklistService,
    private serviceRequest: ServiceRequestService,
    public translate: TranslateService,
    private workshopJobcardCreateService: WorkshopJobcardCreateService,
    private cdr: ChangeDetectorRef,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,

  ) {
    this.partnerData = this.helpersService.getPartnerData();
  }

  ngOnInit() {
    this.ensureDropdownValuesAreSet();

    this.generateServiceTypeGroups();

    if (!this.jobcard) {
      this.getAvailableSlots();
      if (this.jobcard && this.jobcard.scheduleDate) {
        if (moment(this.jobcard.scheduleDate).toDate() < this.minDate) {
          this.getAvailableSlots(this.jobcard.technician && this.jobcard.technician > 0, true);
        } else {
          this.scheduleDate = new Date(this.jobcard.scheduleDate);
        }
      }
    }
    if (this.thirdParties && this.thirdParties.length > 0) {
      this.showThirdParties = true;
    }

    this.getBays();
 
    if (this.jobcard.id) {
      // Debounced backend updates only when editing an existing job
      this.subs.add(
        this.updateQueue$
          .pipe(
            debounceTime(200),
            distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
            switchMap(async patch => {
              //console.log('Patching jobcard with:', patch);
              var data = {
                ...this.jobcard,
                scheduleDate: moment(this.jobcard.scheduleDate).format("YYYY-MM-DDTHH:mm:ss"),
                requiredByDate: this.jobcard.requiredByDate ? moment(this.jobcard.requiredByDate).format("YYYY-MM-DDTHH:mm:ss") : null,
              };
              return await this.serviceRequest.updateWorkshopJobCard(data).subscribe(async (result) => {
                this.jobcardChange.emit({ ...this.jobcard, technicianFullName: result.technicianFullName, technicianName: result.technicianName, thirdPartyName: result.thirdPartyName });
                this.messageService.add({ key: 'jobcard-details-updated', life: 3000, detail: this.translate.instant('bikeshop.calendar.message_job_updated', { JobCardNo: this.jobcard.jobCardNo }) })
              });
            })
          )
          .subscribe()
      );
      // Separate debounced updates for bay tag with longer delay
      this.subs.add(
        this.bayTagUpdateQueue$
          .pipe(
            debounceTime(200), // 1 second delay for bay tag
            distinctUntilChanged()
          )
          .subscribe(bayTag => {
            this.updateQueue$.next({ bayNo: bayTag });
          })
      );
    }

  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.jobcard && this.jobcard.scheduleDate) {
      this.scheduleDate = new Date(this.jobcard.scheduleDate);
    }
    if (this.jobcard && this.jobcard.requiredByDate) {
      this.requiredByDate = new Date(this.jobcard.requiredByDate);
      this.addRequiredByDate = true;
    }
    this.updateLocalServiceTypes();
  }

  // Centralized helper to mutate, emit, and queue backend update
  private applyChange<K extends keyof any>(field: K, value: any) {
    if (this.jobcard.id) {
      this.jobcard = { ...this.jobcard, [field]: value }; // new ref for parent if it binds back
      // this.jobcardChange.emit(this.jobcard);
      // queue minimal patch for backend if editing existing job
      this.updateQueue$.next({ [field]: value });
    } else {
      this.jobcardChange.emit(this.jobcard);
    }
  }

  async onDateChanged(event: any, timeChanged = false) {
    const newDate = event;
    this.jobcard.scheduleDate = newDate;
    if (!timeChanged) {
      var availability = await this.getAvailableSlots(true, true);
      if (!availability.slotsAvailable) {
        this.confirmationService.confirm({
          key: 'availability-error',
          rejectVisible: true,
          acceptLabel: this.translate.instant('bikeshop.modal.shedule_service.use_this_date'),
          rejectLabel: this.translate.instant('bikeshop.modal.shedule_service.next_available_date'),
          header: this.translate.instant('bikeshop.modal.shedule_service.no_slots_for_this_day_title'),
          message: this.translate.instant(
            'bikeshop.modal.shedule_service.no_slots_for_this_day'
          ),
          accept: async () => {
            this.minRequiredByDate = new Date(moment.utc(this.jobcard.scheduleDate).format('YYYY-MM-DDTHH:mm'));
            this.applyChange('scheduleDate', this.jobcard.scheduleDate);
          },
          reject: async () => {
            var availability = await this.getAvailableSlots(false, true, true);
            this.minRequiredByDate = new Date(moment.utc(this.jobcard.scheduleDate).format('YYYY-MM-DDTHH:mm'));
            this.applyChange('scheduleDate', this.jobcard.scheduleDate);
          }
        });
        return;
      }
    }
    this.minRequiredByDate = new Date(moment.utc(this.jobcard.scheduleDate).format('YYYY-MM-DDTHH:mm'));
    this.applyChange('scheduleDate', this.jobcard.scheduleDate);
  }

  onRequiredDateChanged(event: any, dateChanged = false) {
    const newDate = dateChanged ? new Date(moment.utc(event).format("YYYY-MM-DDT12:00:00.000")) : event;
    this.jobcard.requiredByDate = newDate;
    this.applyChange('requiredByDate', newDate);
  }

  updateLocalServiceTypes() {
    this.selectedServiceTypes = this.jobcard.selectedServiceTypes ? this.jobcard.selectedServiceTypes.map(item => item.serviceTypeID) : [];
  }

  private ensureDropdownValuesAreSet() {
    // Force re-assignment to trigger change detection
    if (this.jobcard.technician && this.technicians.length > 0) {
      const technicianExists = this.technicians.find(t => t.value === this.jobcard.technician);
      if (technicianExists) {
        setTimeout(() => {
          // Force re-render of dropdown component
          if (this.technicianDropdown) {
            this.technicianDropdown.writeValue(this.jobcard.technician);
          }
          this.cdr.detectChanges();
        }, 0);
      }
    }

    if (this.jobcard.createdBy && this.systemUsers.length > 0) {
      console.log('Checking createdBy:', this.jobcard.createdBy);
      const userExists = this.systemUsers.find(u => u.value === this.jobcard.createdBy);
      if (userExists) {
        setTimeout(() => {
          // Force re-render of dropdown component
          if (this.createdByDropdown) {
            this.createdByDropdown.writeValue(this.jobcard.createdBy);
          }
          this.cdr.detectChanges();
        }, 0);
      }
    }

    if (this.jobcard.createdBy && this.systemUsers.length > 0) {
      console.log('Checking checkedBy:', this.jobcard.checkedBy);
      const userExists = this.systemUsers.find(u => u.value === this.jobcard.checkedBy);
      if (userExists) {
        setTimeout(() => {
          // Force re-render of dropdown component
          if (this.checkedByDropdown) {
            this.checkedByDropdown.writeValue(this.jobcard.checkedBy);
          }
          this.cdr.detectChanges();
        }, 0);
      }
    }

    if (this.jobcard.thirdPartyID && this.thirdParties?.length > 0) {
      const thirdPartyExists = this.thirdParties.find(u => u.value === this.jobcard.thirdPartyID);
      if (thirdPartyExists) {
        setTimeout(() => {
          // Force re-render of dropdown component
          if (this.thirdPartyDropdown) {
            this.thirdPartyDropdown.writeValue(this.jobcard.thirdPartyID);
          }
          this.cdr.detectChanges();
        }, 0);
      }
    }


    //if (this.bays.length > 0) {
    //  //var value = '';
    //  //this.bay = this.bays.find(x => x.label === this.jobcard.bayNo);
    //  console.log('Updated bay object:', this.bay);
    //}

    //if (this.bays && this.bays.length > 0) {

    //    setTimeout(() => {
    //      // Force re-render of dropdown component
    //      if (this.bayDropdown) {
    //        //this.bayDropdown.writeValue(1);
    //        //console.log('Bay dropdown value set to:', 1);
    //      }
    //      this.cdr.detectChanges();
    //    }, 50);
    //}
  }

  async getAvailableSlots(technicianSpecific = false, force = false, allocateFirstAvailable = false, preferredTechnicianId = 0): Promise<{ jobcard: any, technicians: any[], slotsAvailable: boolean }> {
    try {
      this.loadingDates = true;
      const result = await this.workshopJobcardCreateService.getAvailableSlots(
        this.jobcard,
        this.selectedServiceTypes,
        this.technicians,
        technicianSpecific,
        force,
        allocateFirstAvailable,
        preferredTechnicianId
      ).toPromise();

      this.jobcard = result.jobcard;
      this.technicians = result.technicians;
      this.scheduleDate = new Date(this.jobcard.scheduleDate);
      this.applyChange('scheduleDate', this.jobcard.scheduleDate);
      this.loadingDates = false;

      return result;
    } catch (error) {
      this.loadingDates = false;
      console.error('Error getting available slots:', error);
      //throw error;
    }
  }

  clearRequiredByDate() {
    this.addRequiredByDate = false;
    this.jobcard.requiredByDate = null;
    this.applyChange('requiredByDate', null);
  }

  generateServiceTypeGroups() {
    this.serviceTypeGroups = [];
    var groupName = '';
    this.serviceTypes.sort((x, y) => {
      return x.groupOrder - y.groupOrder || x.groupName - y.groupName;
    })
      .map((serviceType) => {
        serviceType.groupName = serviceType.groupName
          ? serviceType.groupName
          : this.translate.instant('general.not_grouped');
        if (serviceType.groupName !== groupName) {
          groupName = serviceType.groupName;
          this.serviceTypeGroups.push({
            serviceType: serviceType.groupName,
            serviceTypeID: -1,
            disabled: true,
            search: '',
            reverseSearch: '',
          });
        }
        var words = serviceType.serviceType.split(' ');
        var reverseSearch = [];
        if (words) {
          words.forEach((word) => {
            reverseSearch.unshift(word);
          });
        }
        var serviceTypeCost = serviceType.cost;
        this.serviceTypeGroups.push({
          serviceType:
            serviceType.serviceType +
            ' - ' +
            this.helpersService.getPartnerData().CurrencySymbol +
            serviceTypeCost?.toFixed(2),
          serviceTypeID: serviceType.serviceTypeID,
          disabled: false,
          hours: serviceType.hours,
          cost: serviceTypeCost,
          search: serviceType.serviceType,
          reverseSearch: reverseSearch.join(' '),
        });
      });

  }

  serviceTypeSelected(event: any) {
    this.jobcard.selectedServiceTypes = event.value.map(id =>
      this.serviceTypes.find(serviceType => serviceType.serviceTypeID === id)
    ).filter(Boolean); // Remove any undefined values

    var index = event.value.findIndex((id) => {
      return event.itemValue.serviceTypeID === id;
    });
    var quoteUpdated = false;
    var selectedServiceTypes = this.serviceTypes.find((serviceType) => {
      return serviceType.serviceTypeID === event.itemValue.serviceTypeID;
    });
    if (index > -1) {
      if (selectedServiceTypes && selectedServiceTypes.linkedSKUs && selectedServiceTypes.linkedSKUs.length > 0) {
        selectedServiceTypes.linkedSKUs.forEach((item) => {
          this.addToQuote(JSON.parse(JSON.stringify(item)));
          quoteUpdated = true;
        });
      }
    } else {
      if (selectedServiceTypes && selectedServiceTypes.linkedSKUs && selectedServiceTypes.linkedSKUs.length > 0) {
        selectedServiceTypes.linkedSKUs.forEach((item) => {
          var removed = this.removeFromQuote(JSON.parse(JSON.stringify(item)));
          if (removed) {
            quoteUpdated = true;
          }
        });
      }
    }

    this.updateLocalServiceTypes();

    if (!this.jobcard.id) {
      // New jobcard only: when the service-type set changes, reallocate — unless the
      // tech + date were explicitly chosen from the calendar, in which case both must
      // stay locked regardless of the selected service types.
      if (!(this.jobcard.originalTechnician && this.jobcard.originalScheduleDate)) {
        this.getAvailableSlots(false, true, true, this.jobcard.originalTechnician || 0);
      }
      this.jobcard = { ...this.jobcard };
    }

    if (!quoteUpdated || !this.partnerData?.AllowQuoteToUpdateServiceTypes) {
      this.applyChange('selectedServiceTypes', [...(this.jobcard.selectedServiceTypes || [])]);
    }
  }


  addToQuote(item) {
    this.workshopJobcardCreateService.convertSingleToUpperCase(item);
    if (!this.jobcard.quote) {
      this.jobcard.quote = { lineItems: [] }
    }
    if (!this.jobcard.quote.lineItems) {
      this.jobcard.quote.lineItems = [];
    }


    if (this.jobcard.quote.lineItems.findIndex((lineItem) => lineItem.InternalProductID === item.InternalProductID) === -1) {
      this.jobcard.quote.lineItems.push({ ...item, Quantity: 1 });
    } else {
      const index = this.jobcard.quote.lineItems.findIndex((lineItem) => lineItem.InternalProductID === item.InternalProductID);
      this.jobcard.quote.lineItems[index].Quantity += 1;
    }

    this.jobcardChange.emit({ ...this.jobcard });
  }

  removeFromQuote(item) {
    this.workshopJobcardCreateService.convertSingleToUpperCase(item);
    if (!this.jobcard.quote) {
      this.jobcard.quote = { lineItems: [] }
    }
    if (!this.jobcard.quote.lineItems) {
      this.jobcard.quote.lineItems = [];
    }

    var index = this.jobcard.quote.lineItems.findIndex((lineItem) => lineItem.InternalProductID === item.InternalProductID);
    if (index > -1) {
      this.jobcard.quote.lineItems.splice(index, 1);
    }

    this.jobcardChange.emit({ ...this.jobcard });
    return index > -1;
  }

  onTechnicianChanged(technicianId: any) {
    this.applyChange('technician', technicianId);
    //Only do this on new jobs as the availability will try look for a technician matching the date
    if (!this.jobcard.id) {
      this.getAvailableSlots(true, true);
    }
  }

  onCreatedByChanged(userId: any) {
    this.applyChange('createdBy', userId);
  }

  onCheckedByChanged(userId: any) {
    this.jobcard.checkedBy = userId;
    const userExists = this.systemUsers.find(u => u.value === this.jobcard.checkedBy);
    this.jobcard.checkedByName = userExists.name;
    this.applyChange('checkedBy', userId);
    this.applyChange('checkedByName', userExists.name);
  }

  onThirdPartyChanged(thirdPartyId: any) {
    this.applyChange('thirdPartyID', thirdPartyId);
  }

  onThirdPartyResponsibleForPaymentChanged(isResponsible: boolean) {
    this.applyChange('thirdPartyIsResponsibleForPayment', isResponsible);
  }

  onBayTagNoChanged(bayTagNo) {
    console.log('Bay tag changed to:', bayTagNo);
    console.log('Available bays:', this.bays);
    if (this.bays.length > 0) {
      const found = this.bays.find(x => x.id === bayTagNo);
      const label = found?.bayTag ?? bayTagNo;
      this.jobcard.bayNo = label;
      this.bayTagUpdateQueue$.next(label);
    } else {
      this.bayTagUpdateQueue$.next(bayTagNo);
    }
    setTimeout(() => {
      this.getBays();
    }, 1000);
  }

  

  getBays() {

    this.serviceRequest.getBays(this.partnerData.ID).subscribe(res => {
      this.bays = res.map(bay => ({
        ...bay,
        value: bay.id,
        label: bay.bayTag + ' - ' + (bay.itemCount) + ' ' + this.translate.instant("bikeshop.modal.add_edit_user.bikes"),
        itemCount: bay.itemCount,
      }));
      if (this.bays.length > 0) {
        this.bay = this.bays.find(x => x.bayTag === this.jobcard.bayNo)?.value ?? null;
        setTimeout(() => {
          if (this.bayDropdown) {
            this.bayDropdown.writeValue(this.bay);
          }
          this.cdr.detectChanges();
        }, 0);
      }
    });

  }




}
